/**
 * The catalogue — what the business sells.
 *
 * Products and services in one list, because a salon sells both and should not
 * have to decide which of two screens an item belongs on. The stock column is
 * blank for anything that does not track stock, rather than showing a zero
 * that would read as "sold out".
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, PackagePlus, Plus } from 'lucide-react';
import {
  PRODUCT_AVAILABILITIES,
  PRODUCT_AVAILABILITY_LABELS,
  PRODUCT_KINDS,
  PRODUCT_KIND_LABELS,
  type CatalogueItem,
  type ProductCategory,
  type ShopContext,
} from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useRecordLocation } from '../../lib/locationContext';
import { LocationField } from '../../components/LocationField';
import { money } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  Spinner,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

interface Paged<T> {
  data: T[];
  meta: { total: number; page: number; pageSize: number };
}

export function CataloguePage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [formOpen, setFormOpen] = useState(params.get('new') === '1');

  const query = qs({ search: search || undefined, kind: kind || undefined, pageSize: 100 });

  const items = useQuery({
    queryKey: ['catalogue', business?.businessId, query],
    queryFn: () => api.get<Paged<CatalogueItem>>(path(`/products${query}`)),
    enabled: Boolean(business),
  });

  const categories = useQuery({
    queryKey: ['catalogue-categories', business?.businessId],
    queryFn: () => api.get<{ data: ProductCategory[] }>(path('/products/categories')),
    enabled: Boolean(business),
  });

  const context = useQuery({
    queryKey: ['shop-context', business?.businessId],
    queryFn: () => api.get<ShopContext>(path('/shop/context')),
    enabled: Boolean(business),
  });

  const noun = context.data?.catalogueNoun ?? { singular: 'Product', plural: 'Products' };
  const currency = business?.currencyCode ?? 'USD';

  const closeForm = (): void => {
    setFormOpen(false);
    if (params.get('new')) {
      params.delete('new');
      setParams(params, { replace: true });
    }
  };

  return (
    <>
      <PageHeader
        title={noun.plural}
        description="What your business sells"
        action={
          can('products:manage') && (
            <Button icon={<Plus className="size-4" />} onClick={() => setFormOpen(true)}>
              Add {noun.singular.toLowerCase()}
            </Button>
          )
        }
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or SKU"
          />
          <Select
            label="Type"
            options={[
              { value: '', label: 'Everything' },
              ...PRODUCT_KINDS.map((value) => ({ value, label: PRODUCT_KIND_LABELS[value] })),
            ]}
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          />
        </div>
      </Card>

      {items.isPending ? (
        <Card>
          <TableSkeleton rows={6} columns={5} />
        </Card>
      ) : items.error ? (
        <ErrorState message="Could not load the catalogue." onRetry={() => items.refetch()} />
      ) : items.data.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Boxes className="size-6" />}
            title={`No ${noun.plural.toLowerCase()} yet`}
            description={`Add what you sell so you can record sales against it and see which lines actually make money.`}
            action={
              can('products:manage') && (
                <Button
                  icon={<PackagePlus className="size-4" />}
                  onClick={() => setFormOpen(true)}
                >
                  Add your first {noun.singular.toLowerCase()}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 font-medium">Name</th>
                  <th className="px-3 py-2.5 font-medium">Type</th>
                  <th className="px-3 py-2.5 font-medium">Category</th>
                  <th className="px-3 py-2.5 text-right font-medium">Price</th>
                  <th className="px-3 py-2.5 text-right font-medium">Cost</th>
                  <th className="px-3 py-2.5 text-right font-medium">In stock</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.data.data.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-900">{item.name}</p>
                      {item.sku && <p className="text-xs text-slate-500">{item.sku}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {PRODUCT_KIND_LABELS[item.kind]}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{item.categoryName ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-900">
                      {item.unitPrice ? money(item.unitPrice, currency) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-500">
                      {item.costPrice ? money(item.costPrice, currency) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular">
                      {/* Blank, not zero: an untracked item has no quantity,
                          which is a different statement from having none. */}
                      {item.onHand === null ? (
                        <span className="text-xs italic text-slate-400">Not tracked</span>
                      ) : (
                        <span
                          className={
                            item.onHand <= 0
                              ? 'font-medium text-red-700'
                              : item.isLow
                                ? 'font-medium text-amber-700'
                                : 'text-slate-900'
                          }
                        >
                          {item.onHand}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge
                        tone={
                          item.availability === 'available'
                            ? 'success'
                            : item.availability === 'out_of_stock'
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        {PRODUCT_AVAILABILITY_LABELS[item.availability]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ItemForm
        open={formOpen}
        onClose={closeForm}
        noun={noun}
        categories={categories.data?.data ?? []}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
          void queryClient.invalidateQueries({ queryKey: ['shop-context'] });
          closeForm();
        }}
      />
    </>
  );
}

function ItemForm({
  open,
  onClose,
  noun,
  categories,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  noun: { singular: string; plural: string };
  categories: ProductCategory[];
  onCreated: () => void;
}): ReactNode {
  const path = useBusinessPath();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'product' | 'service'>('product');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [description, setDescription] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [availability, setAvailability] = useState('available');
  const [trackStock, setTrackStock] = useState(false);
  const [openingStock, setOpeningStock] = useState('');
  const [reorderLevel, setReorderLevel] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * The product itself is business-wide — a catalogue belongs to the business,
   * not to one shop. An OPENING BALANCE is different: that is stock sitting on
   * a particular shelf, so it has to say which.
   */
  const location = useRecordLocation();
  const opening = trackStock && openingStock ? Number(openingStock) : 0;

  const create = useMutation({
    mutationFn: () =>
      api.post(path('/products'), {
        name: name.trim(),
        kind,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        description: description.trim() || null,
        unitPrice: unitPrice.trim() || null,
        costPrice: costPrice.trim() || null,
        categoryId: categoryId || null,
        availability,
        // A service can never track stock; the server enforces this too.
        trackStock: kind === 'service' ? false : trackStock,
        ...(trackStock && openingStock ? { openingStock: Number(openingStock) } : {}),
        ...(opening > 0 ? { openingStockBranchId: location.branchId } : {}),
        reorderLevel: reorderLevel ? Number(reorderLevel) : null,
      }),
    onSuccess: () => {
      setName('');
      setSku('');
      setBarcode('');
      setDescription('');
      setUnitPrice('');
      setCostPrice('');
      setOpeningStock('');
      setReorderLevel('');
      setTrackStock(false);
      setError(null);
      onCreated();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save this.'),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (opening > 0 && !location.branchId) {
      setError('Choose which location this opening stock is sitting in.');
      return;
    }
    create.mutate();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Add ${noun.singular.toLowerCase()}`} size="lg">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Type"
            options={PRODUCT_KINDS.map((value) => ({
              value,
              label: PRODUCT_KIND_LABELS[value],
            }))}
            value={kind}
            onChange={(event) => setKind(event.target.value as 'product' | 'service')}
            hint={kind === 'service' ? 'Services do not carry stock' : undefined}
          />
          <Select
            label="Category"
            options={[
              { value: '', label: 'Uncategorised' },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Selling price"
            inputMode="decimal"
            value={unitPrice}
            onChange={(event) => setUnitPrice(event.target.value)}
            hint="Leave blank for price on request"
          />
          <Input
            label="Cost price (optional)"
            inputMode="decimal"
            value={costPrice}
            onChange={(event) => setCostPrice(event.target.value)}
            hint="Unlocks gross profit on your reports"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="SKU / reference (optional)"
            value={sku}
            onChange={(event) => setSku(event.target.value)}
          />
          {/*
            Registering the barcode here is what makes a stock count scannable
            later. The field accepts a scanner directly — point it at the
            carton rather than reading the digits off the label.
          */}
          <Input
            label="Barcode (optional)"
            value={barcode}
            onChange={(event) => setBarcode(event.target.value)}
            hint="Scan the carton, or type the code. Used to count stock by scanning."
          />
          <Select
            label="Availability"
            options={PRODUCT_AVAILABILITIES.map((value) => ({
              value,
              label: PRODUCT_AVAILABILITY_LABELS[value],
            }))}
            value={availability}
            onChange={(event) => setAvailability(event.target.value)}
          />
        </div>

        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
        />

        {kind === 'product' && (
          <div className="rounded-lg border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <input
                type="checkbox"
                checked={trackStock}
                onChange={(event) => setTrackStock(event.target.checked)}
                className="size-4 rounded border-slate-300"
              />
              Track stock for this item
            </label>

            {trackStock && (
              <div className="mt-3 space-y-3">
                {opening > 0 && (
                  <LocationField
                    label="Opening stock is at"
                    value={location.branchId}
                    onChange={location.setBranchId}
                    locations={location.locations}
                  />
                )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="Opening stock"
                  type="number"
                  min={0}
                  value={openingStock}
                  onChange={(event) => setOpeningStock(event.target.value)}
                  hint="Recorded as an opening balance you can trace later"
                />
                <Input
                  label="Tell me when it drops to"
                  type="number"
                  min={0}
                  value={reorderLevel}
                  onChange={(event) => setReorderLevel(event.target.value)}
                />
              </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" className="flex-1" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Saving…' : `Add ${noun.singular.toLowerCase()}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
